import { ConfirmationDialogButton } from "@/components/shared/ConfirmationDialog";
import { CardLabel } from "@/components/ui/CardLabel";
import RotatingBorder from "@/components/ui/rotating-border";
import { cn } from "@/utils/cn";
import formatTemplateString from "@/utils/formatTemplateString";
import type { RefObject } from "react";
import { DashboardService } from "../service.dashboard";
import type { TLedgerAmounts } from "../types.dashboard";
import { formatHeadlineAmount } from "../utils/formatHeadlineAmount";
import UpgradeSummary, { type UpgradeSummaryProps } from "./UpgradeSummary";

const COPY = DashboardService.Content.yearlyPlanSaleLedger;

const formatMonths = (count: number) =>
  formatTemplateString(
    count === 1 ? COPY.monthCount.one : COPY.monthCount.other,
    String(count),
  );

export interface YearlyPlanSaleLedgerProps
  extends TLedgerAmounts, Pick<UpgradeSummaryProps, "promo"> {
  /** On the ledger's root, so its impression is tracked when it scrolls into view */
  viewRef?: RefObject<HTMLElement>;
  /** Runs on the CTA click, before the confirm dialog opens */
  onSwitchClick?: () => void;
  /** Runs on confirm; the dialog stays open if it throws */
  onSwitchToYearly: () => void | Promise<void>;
  /** Opens the confirm dialog without a click, e.g. from a deep link */
  isConfirmOpen?: boolean;
  /** Runs whenever the confirm dialog opens or closes */
  onConfirmOpenChange?: (open: boolean) => void;
  /** Holds the confirm action while the estimate refetches */
  isEstimating?: boolean;
  disabled?: boolean;
  className?: string;
}

const CELL_TEXT =
  "font-archivo text-sm leading-[23.8px] font-light tracking-[0.14px]";

interface LedgerRowProps {
  name: string;
  tag?: string;
  price: string;
  charged: string;
  outcome: string;
  outcomeClassName?: string;
  className?: string;
}

/** Below md each row stacks into two lines; from md it joins the table's columns */
const LedgerRow = ({
  name,
  tag,
  price,
  charged,
  outcome,
  outcomeClassName = "text-type-black",
  className,
}: LedgerRowProps) => (
  <div
    role="row"
    className={cn(
      "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1 rounded-lg border border-edge-card px-4 py-3.5 md:col-span-4 md:grid-cols-subgrid",
      className,
    )}
  >
    <div role="rowheader" className="flex items-center gap-2">
      <span className="font-archivo text-[15px] leading-5 font-medium text-type-black">
        {name}
      </span>
      {tag && (
        <span className="font-archivo text-xs font-medium text-type-2">
          {tag}
        </span>
      )}
    </div>
    <div role="cell" className="text-right text-type-black md:text-left">
      <span className="font-archivo text-base font-medium tabular-nums">
        {price}
      </span>
      <span className="font-archivo text-sm font-light">{COPY.perMonth}</span>
    </div>
    <div role="cell" className={cn(CELL_TEXT, "text-type-black")}>
      {charged}
    </div>
    <div role="cell" className={cn(CELL_TEXT, "text-right", outcomeClassName)}>
      {outcome}
    </div>
  </div>
);

const YearlyPlanSaleLedger = ({
  currency,
  monthlyPrice,
  monthsPaid,
  yearlyPrice,
  paidSoFar,
  yearlyAsMonthly,
  savedSoFar,
  savedPerYear,
  proratedCredit,
  discount,
  aftershootCredit,
  amountDueToday,
  promo,
  viewRef,
  onSwitchClick,
  onSwitchToYearly,
  isConfirmOpen,
  onConfirmOpenChange,
  isEstimating = false,
  disabled = false,
  className,
}: YearlyPlanSaleLedgerProps) => {
  const formatAmount = (amount: number) =>
    formatHeadlineAmount(amount, currency);

  const columnHeaders = [
    COPY.table.billing,
    COPY.table.price,
    COPY.table.charged,
    formatTemplateString(COPY.table.over, formatMonths(monthsPaid)),
  ];

  return (
    <section
      ref={viewRef}
      className={cn(
        "flex w-full flex-col gap-4 rounded-lg border border-edge-card bg-surface-white p-4 sm:px-6 sm:py-5",
        className,
      )}
    >
      <header className="flex flex-col gap-1">
        <h2 className="font-archivo text-xl leading-7 font-normal text-type-black">
          {formatTemplateString(COPY.title, formatAmount(savedSoFar))}
        </h2>
        <p className="font-archivo text-sm leading-[22.4px] font-light tracking-[0.16px] text-type-1 sm:text-base">
          {COPY.description}
        </p>
      </header>

      {/* Rows share the column tracks through subgrid */}
      <div
        role="table"
        aria-label={COPY.table.label}
        className="flex flex-col gap-2 md:grid md:grid-cols-[minmax(0,16.5rem)_minmax(0,9.25rem)_1fr_auto] md:gap-x-4"
      >
        <div
          role="row"
          className="hidden border border-transparent px-4 md:col-span-4 md:grid md:grid-cols-subgrid"
        >
          {columnHeaders.map((header) => (
            <div key={header} role="columnheader" className="last:text-right">
              <CardLabel text={header} color="gray" className="leading-4" />
            </div>
          ))}
        </div>

        <LedgerRow
          name={COPY.monthly.name}
          tag={COPY.monthly.current}
          price={formatAmount(monthlyPrice)}
          charged={COPY.monthly.charged}
          outcome={formatTemplateString(
            COPY.monthly.paid,
            formatAmount(paidSoFar),
          )}
        />
        {/* Both wrappers subgrid too, so the row keeps the table's columns */}
        <RotatingBorder
          className="md:col-span-4 md:grid md:grid-cols-subgrid"
          cardClass="md:col-span-4 md:grid md:grid-cols-subgrid"
          borderWidth={1}
        >
          <LedgerRow
            name={COPY.annual.name}
            price={formatAmount(yearlyAsMonthly)}
            charged={formatTemplateString(
              COPY.annual.charged,
              formatAmount(yearlyPrice),
            )}
            outcome={formatTemplateString(
              COPY.annual.saved,
              formatAmount(savedSoFar),
              formatAmount(savedPerYear),
            )}
            outcomeClassName="text-tag-green-primary"
            className="border-0"
          />
        </RotatingBorder>
      </div>

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end sm:gap-4">
        <p className="text-center font-archivo text-sm leading-[23.8px] font-light tracking-[0.14px] text-type-2">
          {formatTemplateString(
            COPY.switchToday.renews,
            formatAmount(yearlyPrice),
          )}
        </p>

        <ConfirmationDialogButton
          variant="accent"
          className="h-10 w-full min-w-24 px-4 text-sm leading-5 sm:w-auto"
          disabled={disabled}
          onClick={onSwitchClick}
          text={COPY.switchToday.label}
          dialogContent={
            <UpgradeSummary
              currency={currency}
              yearlyPrice={yearlyPrice}
              proratedCredit={proratedCredit}
              discount={discount}
              aftershootCredit={aftershootCredit}
              amountDueToday={amountDueToday}
              promo={promo}
            />
          }
          primaryAction={{
            label: COPY.switchToday.confirm,
            onClick: onSwitchToYearly,
            isLoading: isEstimating,
          }}
          errorMessage={COPY.switchToday.error}
          open={isConfirmOpen}
          onOpenChange={onConfirmOpenChange}
          dialogClassName="max-w-172.5"
        >
          {COPY.switchToday.cta}
        </ConfirmationDialogButton>
      </div>
    </section>
  );
};

export default YearlyPlanSaleLedger;
